import React from 'react';
import { createStyles, FormControl, FormControlLabel, Radio, RadioGroup, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { PaymentMethodType, PrimaryContactType } from 'interfaces/index';
import { CompanyContactPayload } from 'interfaces/companyContact';
import { makeStyles } from '@material-ui/core/styles';
import { LoadingOverlay } from '@pec/aion-ui-components/components/LoadingOverlay';
import PrepopulateFields from 'helpers/prepopulateFields';
import ZuoraComponent from './ZuoraComponent';

interface Props {
  contactData: CompanyContactPayload;
  loading: boolean;
}

export const AddNewPaymentMethod: React.FC<Props> = ({ contactData, loading }) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const [type, setType] = React.useState(PaymentMethodType.CreditCard);
  const [contactType, setContactType] = React.useState(PrimaryContactType.Billing);
  const [primaryContact, setPrimaryContact] = React.useState<PrepopulateFields | null>(null);
  const [onLoad, setOnLoad] = React.useState(true);

  React.useEffect(() => {
    if (!loading && contactData.id && onLoad) {
      setPrimaryContact(
        new PrepopulateFields({
          firstName: contactData.billingContact.firstName,
          lastName: contactData.billingContact.lastName,
          country: contactData.billingAddress.countryIso3,
          state: contactData.billingAddress.state,
          city: contactData.billingAddress.city,
          address1: contactData.billingAddress.address1,
          address2: contactData.billingAddress.address2,
          postalCode: contactData.billingAddress.postalCode,
          email: contactData.billingContact.email,
          phone: contactData.billingContact.phone
        })
      );
      setOnLoad(false);
    }
  }, [loading, contactData, onLoad]);

  const handleChangeType = (event: React.ChangeEvent<HTMLInputElement>) => {
    setType(PaymentMethodType[(event.target as HTMLInputElement).value]);
  };

  const handleChangeContact = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value === PrimaryContactType.Billing) {
      setPrimaryContact(
        new PrepopulateFields({
          firstName: contactData.billingContact.firstName,
          lastName: contactData.billingContact.lastName,
          country: contactData.billingAddress.countryIso3,
          state: contactData.billingAddress.state,
          city: contactData.billingAddress.city,
          address1: contactData.billingAddress.address1,
          address2: contactData.billingAddress.address2,
          postalCode: contactData.billingAddress.postalCode,
          email: contactData.billingContact.email,
          phone: contactData.billingContact.phone
        })
      );
    }
    if (event.target.value === PrimaryContactType.Company) {
      setPrimaryContact(
        new PrepopulateFields({
          firstName: contactData.administratorContact.firstName,
          lastName: contactData.administratorContact.lastName,
          country: contactData.physicalAddress.countryIso3,
          state: contactData.physicalAddress.state,
          city: contactData.physicalAddress.city,
          address1: contactData.physicalAddress.address1,
          address2: contactData.physicalAddress.address2,
          postalCode: contactData.physicalAddress.postalCode,
          email: contactData.administratorContact.email,
          phone: contactData.administratorContact.phone
        })
      );
    }

    if (event.target.value === PrimaryContactType.New) {
      setPrimaryContact(null);
    }

    setContactType(PrimaryContactType[event.target.value]);
  };

  return (
    <div className={classes.addNewContainer}>
      <FormControl fullWidth component="fieldset">
        <RadioGroup
          row
          aria-label={t(
            'subscriber:paymentMethodModal.addNewPaymentMethod.radioGroupPaymentMethod.paymentMethodType',
            'Payment Method Type'
          )}
          name="paymentMethodType"
          value={type}
          onChange={handleChangeType}
        >
          <FormControlLabel
            className={classes.radioGroup}
            value={PaymentMethodType.CreditCard}
            control={<Radio color="primary" />}
            label={t(
              'subscriber:paymentMethodModal.addNewPaymentMethod.radioGroupPaymentMethod.labelCreditCard',
              'Credit Card'
            )}
          />
          <FormControlLabel
            value={PaymentMethodType.ACH}
            control={<Radio color="primary" />}
            label={t(
              'subscriber:paymentMethodModal.addNewPaymentMethod.radioGroupPaymentMethod.labelACH',
              'ACH/E-check'
            )}
          />
        </RadioGroup>
      </FormControl>
      {type === PaymentMethodType.CreditCard && (
        <div className={classes.addressesContainer}>
          <Typography variant="subtitle1" className={classes.title}>
            {t('subscriber:paymentMethodModal.addNewPaymentMethod.billingContactTitle', 'Billing Address')}
          </Typography>
          <Typography variant="body1">
            {t(
              'subscriber:paymentMethodModal.addNewPaymentMethod.billingContactDescription',
              "Enter the information for the account's Primary Billing Contact."
            )}
          </Typography>
          <FormControl fullWidth component="fieldset">
            <RadioGroup
              aria-label={t('subscriber:paymentMethodModal.addNewPaymentMethod.billingContact', 'Billing Contact')}
              name="billingContact"
              value={contactType}
              onChange={handleChangeContact}
            >
              <LoadingOverlay isLoading={loading}>
                <div className={classes.addressSection}>
                  <FormControlLabel
                    value={PrimaryContactType.Billing}
                    control={<Radio color="primary" />}
                    label={t(
                      'subscriber:paymentMethodModal.addNewPaymentMethod.radioGroupPrimaryContact.billing',
                      'Same as Primary Billing Contact Address'
                    )}
                  />
                  <div className={classes.address}>
                    <div>{`${contactData.billingContact.firstName} ${contactData.billingContact.lastName}`}</div>
                    <div>{`${contactData.billingAddress.address1}`}</div>
                    <div>{`${contactData.billingAddress.city}, ${contactData.billingAddress.state}, ${contactData.billingAddress.country}, ${contactData.billingAddress.postalCode}`}</div>
                  </div>
                </div>
                <div className={classes.addressSection}>
                  <FormControlLabel
                    value={PrimaryContactType.Company}
                    control={<Radio color="primary" />}
                    label={t(
                      'subscriber:paymentMethodModal.addNewPaymentMethod.radioGroupPrimaryContact.company',
                      'Same as Physical Company Address'
                    )}
                  />
                  <div className={classes.address}>
                    <div>{`${contactData.administratorContact.firstName} ${contactData.administratorContact.lastName}`}</div>
                    <div>{`${contactData.physicalAddress.address1}`}</div>
                    <div>{`${contactData.physicalAddress.city}, ${contactData.physicalAddress.state}, ${contactData.physicalAddress.country}, ${contactData.physicalAddress.postalCode}`}</div>
                  </div>
                </div>
                <div className={classes.addressSection}>
                  <FormControlLabel
                    value={PrimaryContactType.New}
                    control={<Radio color="primary" />}
                    label={t(
                      'subscriber:paymentMethodModal.addNewPaymentMethod.radioGroupPrimaryContact.new',
                      'Add Another Address'
                    )}
                  />
                </div>
                <ZuoraComponent
                  accountId={contactData.accountId}
                  methodType={PaymentMethodType.CreditCard}
                  prepopulateFields={primaryContact}
                />
              </LoadingOverlay>
            </RadioGroup>
          </FormControl>
        </div>
      )}
      {type === PaymentMethodType.ACH && (
        <ZuoraComponent accountId={contactData.accountId} methodType={PaymentMethodType.ACH} />
      )}
    </div>
  );
};

const useStyles = makeStyles(() =>
  createStyles({
    addNewContainer: {
      borderTop: '1px solid var(--on-surface-outline-000000-12-)'
    },
    radioGroup: {
      padding: '15px'
    },
    title: {
      fontWeight: 500,
      marginBottom: '16px'
    },
    description: {},
    addressLabel: {},
    addressesContainer: {
      border: '1px solid var(--on-surface-outline-000000-12-)',
      borderRadius: '4px',
      padding: '24px'
    },
    address: {
      border: '1px solid var(--on-surface-outline-000000-12-)',
      backgroundColor: 'var(--light-gray-surface)',
      borderRadius: '4px',
      padding: '17px 0 11px 16px'
    },
    addressSection: {
      marginTop: '14px'
    }
  })
);
